import { Dungeon } from '../dungeon/Dungeon';
import { CellType } from '../types';
import { Camera } from '../player/Camera';
import {
  vertexShaderSource,
  getFragmentShaderSource,
  createShader,
  createProgram,
  DEFAULT_MAX_LIGHTS
} from './shaders';
import {
  generateWallGeometry,
  generateFloorGeometry,
  generateCeilingGeometry,
  createVAO
} from './geometry';
import { Star, generateStarGeometry } from '../entities/Star';
import { SpeedBoost, generateSpeedBoostGeometry } from '../entities/SpeedBoost';
import { generateCeilingLightGeometry } from '../entities/CeilingLight';
import { WebTrap, generateWebTrapGeometry } from '../entities/WebTrap';
import { Breadcrumbs, generateBreadcrumbsPickupGeometry, generateFootprintGeometry } from '../entities/Breadcrumbs';
import { Arrow, generateFloatingArrowGeometry } from '../entities/Arrow';

// Import textures
import floorTextureUrl from '../textures/floor.webp';
import ceilingTextureUrl from '../textures/ceiling.webp';
import wall1TextureUrl from '../textures/walls/wall1.webp';
import wall2TextureUrl from '../textures/walls/wall2.webp';
import wall3TextureUrl from '../textures/walls/wall3.webp';
import wall4TextureUrl from '../textures/walls/wall4.webp';

interface UniformLocations {
  model: WebGLUniformLocation | null;
  view: WebGLUniformLocation | null;
  projection: WebGLUniformLocation | null;
  color: WebGLUniformLocation | null;
  ambientColor: WebGLUniformLocation | null;
  playerLightPos: WebGLUniformLocation | null;
  playerLightColor: WebGLUniformLocation | null;
  playerLightRadius: WebGLUniformLocation | null;
  starLightPositions: WebGLUniformLocation | null;
  starLightColors: WebGLUniformLocation | null;
  starLightCount: WebGLUniformLocation | null;
  starLightRadius: WebGLUniformLocation | null;
  debugLightPos: WebGLUniformLocation | null;
  debugLightColor: WebGLUniformLocation | null;
  debugLightRadius: WebGLUniformLocation | null;
  debugLightEnabled: WebGLUniformLocation | null;
  dungeonGrid: WebGLUniformLocation | null;
  dungeonSize: WebGLUniformLocation | null;
  cellSize: WebGLUniformLocation | null;
  surfaceTexture: WebGLUniformLocation | null;
  useTexture: WebGLUniformLocation | null;
  wallTextures: (WebGLUniformLocation | null)[];
  useWallTexture: WebGLUniformLocation | null;
  useSpecular: WebGLUniformLocation | null;
  viewPos: WebGLUniformLocation | null;
  shininess: WebGLUniformLocation | null;
  useCubemap: WebGLUniformLocation | null;
  cubemap: WebGLUniformLocation | null;
  reflectivity: WebGLUniformLocation | null;
  ceilingLightStartIndex: WebGLUniformLocation | null;
}

interface GeometryData {
  vao: WebGLVertexArrayObject;
  vertexCount: number;
}

export class Renderer {
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private uniforms: UniformLocations;
  private wallNorthGeometry: GeometryData | null = null;
  private wallSouthGeometry: GeometryData | null = null;
  private wallEastGeometry: GeometryData | null = null;
  private wallWestGeometry: GeometryData | null = null;
  private floorGeometry: GeometryData | null = null;
  private ceilingGeometry: GeometryData | null = null;
  private starGeometry: GeometryData | null = null;
  private speedBoostGeometry: GeometryData | null = null;
  private ceilingLightGeometry: GeometryData | null = null;
  private webTrapGeometry: GeometryData | null = null;
  private breadcrumbsPickupGeometry: GeometryData | null = null;
  private footprintGeometry: GeometryData | null = null;
  private floatingArrowGeometry: GeometryData | null = null;
  private stars: Star[] = [];
  private speedBoost: SpeedBoost | null = null;
  private webTrap: WebTrap | null = null;
  private breadcrumbs: Breadcrumbs | null = null;
  private arrow: Arrow | null = null;
  private floatingArrowPosition: { x: number; y: number; z: number } | null = null;
  private floatingArrowYaw = 0;
  private starLightPositions: Float32Array;
  private starLightColors: Float32Array;
  private starColors: number[][] = [];  // Full brightness colors for star geometry
  private projectionMatrix: Float32Array;
  private dungeonTexture: WebGLTexture | null = null;
  private dungeonWidth = 0;
  private dungeonHeight = 0;
  private cellSize = 1;
  private time = 0;
  private maxLights = DEFAULT_MAX_LIGHTS;

  // Surface textures
  private floorTexture: WebGLTexture | null = null;
  private ceilingTexture: WebGLTexture | null = null;
  private wallTextures: (WebGLTexture | null)[] = [null, null, null, null];
  private texturesLoaded = false;
  private texturesReady: Promise<void>;

  // Colors - used as fallback
  private readonly floorColor = [0.25, 0.25, 0.28];
  private readonly ceilingColor = [0.35, 0.35, 0.38];
  private readonly ambientColor = [0, 0, 0];
  private readonly playerLightColor = [1.0, 0.95, 0.8];
  private readonly playerLightRadius = 8.0;
  private readonly starLightRadius = 10.0;

  // Debug light state
  private debugLightPos = [0, 0, 0];
  private debugLightColor = [0, 1, 1];  // Cyan
  private debugLightRadius = 5.0;
  private debugLightEnabled = false;

  // Player light state
  private playerLightEnabled = true;

  // Ceiling lights (world-space positions)
  private ceilingLights: { x: number; z: number }[] = [];
  private ceilingLightFlicker: { on: boolean; nextToggle: number; flashUntil: number }[] = [];
  private onCeilingLightOff: ((lightX: number, lightZ: number) => void) | null = null;

  // Cubemap reflection for speed boost
  private cubemapTexture: WebGLTexture | null = null;
  private dummyCubemap: WebGLTexture | null = null; // 1x1 cubemap for when real one is render target
  private cubemapFramebuffer: WebGLFramebuffer | null = null;
  private cubemapDepthBuffer: WebGLRenderbuffer | null = null;
  private readonly cubemapSize = 128; // Resolution per face

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2');
    if (!gl) {
      throw new Error('WebGL2 not supported');
    }
    this.gl = gl;

    // Build initial shader program
    this.program = null!;
    this.uniforms = null!;
    this.buildShaderProgram(this.maxLights);

    // Initialize projection matrix and star light arrays
    this.projectionMatrix = new Float32Array(16);
    this.starLightPositions = new Float32Array(this.maxLights * 3);
    this.starLightColors = new Float32Array(this.maxLights * 3);
    this.updateProjection(canvas.width, canvas.height);

    // Configure WebGL
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.clearColor(0.05, 0.05, 0.08, 1.0);

    // Load textures
    this.texturesReady = this.loadTextures();

    // Initialize cubemap for reflections
    this.initCubemap();
  }

  waitForTextures(): Promise<void> {
    return this.texturesReady;
  }

  private buildShaderProgram(maxLights: number): void {
    const gl = this.gl;

    // Delete old program if rebuilding
    if (this.program) {
      gl.deleteProgram(this.program);
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, getFragmentShaderSource(maxLights));

    if (!vertexShader || !fragmentShader) {
      throw new Error('Failed to create shaders');
    }

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      throw new Error('Failed to create shader program');
    }
    this.program = program;

    this.uniforms = {
      model: gl.getUniformLocation(program, 'u_model'),
      view: gl.getUniformLocation(program, 'u_view'),
      projection: gl.getUniformLocation(program, 'u_projection'),
      color: gl.getUniformLocation(program, 'u_color'),
      ambientColor: gl.getUniformLocation(program, 'u_ambientColor'),
      playerLightPos: gl.getUniformLocation(program, 'u_playerLightPos'),
      playerLightColor: gl.getUniformLocation(program, 'u_playerLightColor'),
      playerLightRadius: gl.getUniformLocation(program, 'u_playerLightRadius'),
      starLightPositions: gl.getUniformLocation(program, 'u_starLightPositions'),
      starLightColors: gl.getUniformLocation(program, 'u_starLightColors'),
      starLightCount: gl.getUniformLocation(program, 'u_starLightCount'),
      starLightRadius: gl.getUniformLocation(program, 'u_starLightRadius'),
      debugLightPos: gl.getUniformLocation(program, 'u_debugLightPos'),
      debugLightColor: gl.getUniformLocation(program, 'u_debugLightColor'),
      debugLightRadius: gl.getUniformLocation(program, 'u_debugLightRadius'),
      debugLightEnabled: gl.getUniformLocation(program, 'u_debugLightEnabled'),
      dungeonGrid: gl.getUniformLocation(program, 'u_dungeonGrid'),
      dungeonSize: gl.getUniformLocation(program, 'u_dungeonSize'),
      cellSize: gl.getUniformLocation(program, 'u_cellSize'),
      surfaceTexture: gl.getUniformLocation(program, 'u_surfaceTexture'),
      useTexture: gl.getUniformLocation(program, 'u_useTexture'),
      wallTextures: [
        gl.getUniformLocation(program, 'u_wallTextures[0]'),
        gl.getUniformLocation(program, 'u_wallTextures[1]'),
        gl.getUniformLocation(program, 'u_wallTextures[2]'),
        gl.getUniformLocation(program, 'u_wallTextures[3]')
      ],
      useWallTexture: gl.getUniformLocation(program, 'u_useWallTexture'),
      useSpecular: gl.getUniformLocation(program, 'u_useSpecular'),
      viewPos: gl.getUniformLocation(program, 'u_viewPos'),
      shininess: gl.getUniformLocation(program, 'u_shininess'),
      useCubemap: gl.getUniformLocation(program, 'u_useCubemap'),
      cubemap: gl.getUniformLocation(program, 'u_cubemap'),
      reflectivity: gl.getUniformLocation(program, 'u_reflectivity'),
      ceilingLightStartIndex: gl.getUniformLocation(program, 'u_ceilingLightStartIndex')
    };

    gl.useProgram(this.program);
    if (this.uniforms.cubemap !== null) {
      gl.uniform1i(this.uniforms.cubemap, 6);
    }

    this.maxLights = maxLights;
  }

  private ensureLightCapacity(needed: number): void {
    if (needed <= this.maxLights) return;

    this.buildShaderProgram(needed);
    this.starLightPositions = new Float32Array(needed * 3);
    this.starLightColors = new Float32Array(needed * 3);
  }

  private initCubemap(): void {
    const gl = this.gl;
    const size = this.cubemapSize;

    // Create cubemap texture
    this.cubemapTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.cubemapTexture);

    // Allocate storage for each face
    for (let i = 0; i < 6; i++) {
      gl.texImage2D(
        gl.TEXTURE_CUBE_MAP_POSITIVE_X + i,
        0,
        gl.RGBA,
        size,
        size,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null
      );
    }

    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

    // Create framebuffer
    this.cubemapFramebuffer = gl.createFramebuffer();

    // Create depth renderbuffer
    this.cubemapDepthBuffer = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.cubemapDepthBuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, size, size);

    // Create a 1x1 dummy cubemap to bind when the real cubemap is a render target
    this.dummyCubemap = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.dummyCubemap);
    const blackPixel = new Uint8Array([0, 0, 0, 255]);
    for (let i = 0; i < 6; i++) {
      gl.texImage2D(
        gl.TEXTURE_CUBE_MAP_POSITIVE_X + i,
        0,
        gl.RGBA,
        1, 1, 0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        blackPixel
      );
    }
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  }

  private async loadTextures(): Promise<void> {
    const gl = this.gl;

    const loadTexture = (url: string): Promise<WebGLTexture | null> => {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          const texture = gl.createTexture();
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
          gl.generateMipmap(gl.TEXTURE_2D);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
          resolve(texture);
        };
        image.onerror = () => {
          console.error('Failed to load texture:', url);
          resolve(null);
        };
        image.src = url;
      });
    };

    // Load all textures in parallel
    const [floor, ceiling, wall1, wall2, wall3, wall4] = await Promise.all([
      loadTexture(floorTextureUrl),
      loadTexture(ceilingTextureUrl),
      loadTexture(wall1TextureUrl),
      loadTexture(wall2TextureUrl),
      loadTexture(wall3TextureUrl),
      loadTexture(wall4TextureUrl)
    ]);

    this.floorTexture = floor;
    this.ceilingTexture = ceiling;
    this.wallTextures = [wall1, wall2, wall3, wall4];
    this.texturesLoaded = true;
  }

  setupDungeon(dungeon: Dungeon): void {
    const gl = this.gl;

    // Store dungeon dimensions
    this.dungeonWidth = dungeon.width;
    this.dungeonHeight = dungeon.height;
    this.cellSize = dungeon.cellSize;

    // Create dungeon grid texture for shadow casting
    const gridData = new Uint8Array(dungeon.width * dungeon.height);
    for (let z = 0; z < dungeon.height; z++) {
      for (let x = 0; x < dungeon.width; x++) {
        const cell = dungeon.getCell(x, z);
        // 255 = open (light passes), 0 = solid (blocks light)
        gridData[z * dungeon.width + x] = (cell && cell.type === CellType.OPEN) ? 255 : 0;
      }
    }

    // Delete old texture if exists
    if (this.dungeonTexture) {
      gl.deleteTexture(this.dungeonTexture);
    }

    // Create texture
    this.dungeonTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.dungeonTexture);
    // Set alignment to 1 byte since R8 format rows may not be 4-byte aligned
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.R8,
      dungeon.width,
      dungeon.height,
      0,
      gl.RED,
      gl.UNSIGNED_BYTE,
      gridData
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // Generate wall geometry
    const wallGeo = generateWallGeometry(dungeon);
    const floorGeo = generateFloorGeometry(dungeon);
    const ceilingGeo = generateCeilingGeometry(dungeon);

    const wallNorthVAO = createVAO(gl, wallGeo.north);
    const wallSouthVAO = createVAO(gl, wallGeo.south);
    const wallEastVAO = createVAO(gl, wallGeo.east);
    const wallWestVAO = createVAO(gl, wallGeo.west);
    const floorVAO = createVAO(gl, floorGeo);
    const ceilingVAO = createVAO(gl, ceilingGeo);

    if (wallNorthVAO) {
      this.wallNorthGeometry = { vao: wallNorthVAO, vertexCount: wallGeo.north.vertexCount };
    }
    if (wallSouthVAO) {
      this.wallSouthGeometry = { vao: wallSouthVAO, vertexCount: wallGeo.south.vertexCount };
    }
    if (wallEastVAO) {
      this.wallEastGeometry = { vao: wallEastVAO, vertexCount: wallGeo.east.vertexCount };
    }
    if (wallWestVAO) {
      this.wallWestGeometry = { vao: wallWestVAO, vertexCount: wallGeo.west.vertexCount };
    }
    if (floorVAO) {
      this.floorGeometry = { vao: floorVAO, vertexCount: floorGeo.vertexCount };
    }
    if (ceilingVAO) {
      this.ceilingGeometry = { vao: ceilingVAO, vertexCount: ceilingGeo.vertexCount };
    }
  }

  setupStars(stars: Star[]): void {
    this.stars = stars;

    // Create star geometry VAO
    const starGeo = generateStarGeometry();
    const starVAO = createVAO(this.gl, starGeo);
    if (starVAO) {
      this.starGeometry = { vao: starVAO, vertexCount: starGeo.vertexCount };
    }

    // Generate unique colors for each star using HSL spectrum
    this.starColors = [];
    for (let i = 0; i < stars.length; i++) {
      // Distribute hues evenly across the spectrum
      const hue = (i / stars.length) * 360;
      const [r, g, b] = hslToRgb(hue, 1.0, 0.5);  // Full saturation, medium lightness
      this.starColors.push([r, g, b]);
    }

    // Update star light positions and colors (light is on ceiling above star)
    for (let i = 0; i < stars.length && i < this.maxLights; i++) {
      this.starLightPositions[i * 3] = stars[i].position.x;
      this.starLightPositions[i * 3 + 1] = 1.0;  // Ceiling height
      this.starLightPositions[i * 3 + 2] = stars[i].position.z;

      // Use dimmer version for the light (multiply by 0.5)
      const color = this.starColors[i];
      this.starLightColors[i * 3] = color[0] * 0.5;
      this.starLightColors[i * 3 + 1] = color[1] * 0.5;
      this.starLightColors[i * 3 + 2] = color[2] * 0.5;
    }
  }

  setupSpeedBoost(speedBoost: SpeedBoost | null): void {
    this.speedBoost = speedBoost;

    // Create speed boost geometry VAO if not already created
    if (!this.speedBoostGeometry) {
      const boostGeo = generateSpeedBoostGeometry();
      const boostVAO = createVAO(this.gl, boostGeo);
      if (boostVAO) {
        this.speedBoostGeometry = { vao: boostVAO, vertexCount: boostGeo.vertexCount };
      }
    }
  }

  setupCeilingLights(positions: { x: number; z: number }[]): void {
    this.ceilingLights = positions;

    // Ensure shader can handle all lights: stars + 1 speed boost + ceiling lights
    const totalNeeded = this.stars.length + 1 + positions.length;
    this.ensureLightCapacity(totalNeeded);

    // Initialize flicker state with random stagger so lights don't sync
    this.ceilingLightFlicker = positions.map(() => ({
      on: true,
      nextToggle: Math.random() * 5 + 1,
      flashUntil: 0
    }));

    // Create ceiling light fixture geometry VAO if not already created
    if (!this.ceilingLightGeometry) {
      const geo = generateCeilingLightGeometry();
      const vao = createVAO(this.gl, geo);
      if (vao) {
        this.ceilingLightGeometry = { vao, vertexCount: geo.vertexCount };
      }
    }
  }

  setupWebTrap(webTrap: WebTrap | null): void {
    this.webTrap = webTrap;
    if (!this.webTrapGeometry) {
      const geo = generateWebTrapGeometry();
      const vao = createVAO(this.gl, geo);
      if (vao) {
        this.webTrapGeometry = { vao, vertexCount: geo.vertexCount };
      }
    }
  }

  setupTurnaroundTrap(): void {
    // Turnaround trap is invisible — no geometry needed
  }

  setupBreadcrumbs(breadcrumbs: Breadcrumbs | null): void {
    this.breadcrumbs = breadcrumbs;
    if (!this.breadcrumbsPickupGeometry) {
      const geo = generateBreadcrumbsPickupGeometry();
      const vao = createVAO(this.gl, geo);
      if (vao) {
        this.breadcrumbsPickupGeometry = { vao, vertexCount: geo.vertexCount };
      }
    }
    if (!this.footprintGeometry) {
      const geo = generateFootprintGeometry();
      const vao = createVAO(this.gl, geo);
      if (vao) {
        this.footprintGeometry = { vao, vertexCount: geo.vertexCount };
      }
    }
  }

  setupArrow(arrow: Arrow | null): void {
    this.arrow = arrow;
    if (!this.floatingArrowGeometry) {
      const geo = generateFloatingArrowGeometry();
      const vao = createVAO(this.gl, geo);
      if (vao) {
        this.floatingArrowGeometry = { vao, vertexCount: geo.vertexCount };
      }
    }
  }

  updateFloatingArrow(pos: { x: number; y: number; z: number }, yaw: number): void {
    this.floatingArrowPosition = pos;
    this.floatingArrowYaw = yaw;
  }

  clearFloatingArrow(): void {
    this.floatingArrowPosition = null;
  }

  setOnCeilingLightOff(callback: ((lightX: number, lightZ: number) => void) | null): void {
    this.onCeilingLightOff = callback;
  }

  setDebugLight(x: number, y: number, z: number, color: [number, number, number], radius: number): void {
    this.debugLightPos = [x, y, z];
    this.debugLightColor = color;
    this.debugLightRadius = radius;
    this.debugLightEnabled = true;
  }

  disableDebugLight(): void {
    this.debugLightEnabled = false;
  }

  togglePlayerLight(): void {
    this.playerLightEnabled = !this.playerLightEnabled;
  }

  updateProjection(width: number, height: number): void {
    const aspect = width / height;
    const fov = Math.PI / 3; // 60 degrees
    const near = 0.01;
    const far = 100;

    perspectiveMatrix(this.projectionMatrix, fov, aspect, near, far);
  }

  resize(width: number, height: number): void {
    this.gl.viewport(0, 0, width, height);
    this.updateProjection(width, height);
  }

  private renderToCubemap(position: { x: number; y: number; z: number }): void {
    const gl = this.gl;
    const size = this.cubemapSize;

    if (!this.cubemapFramebuffer || !this.cubemapTexture || !this.cubemapDepthBuffer || !this.dummyCubemap) {
      return;
    }

    // Unbind the real cubemap from unit 6 before rendering to it
    // Bind the dummy cubemap instead so the shader has a valid cubemap
    const gl2 = gl;
    gl2.activeTexture(gl2.TEXTURE6);
    gl2.bindTexture(gl2.TEXTURE_2D, null);
    gl2.bindTexture(gl2.TEXTURE_CUBE_MAP, this.dummyCubemap);

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.cubemapFramebuffer);
    gl.viewport(0, 0, size, size);

    // Attach depth buffer
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.cubemapDepthBuffer);

    // 90 degree FOV for cubemap faces
    const cubemapProjection = new Float32Array(16);
    perspectiveMatrix(cubemapProjection, Math.PI / 2, 1.0, 0.1, 50.0);

    // Direction vectors for each cube face
    const faceDirections = [
      { target: [1, 0, 0], up: [0, -1, 0] },   // +X
      { target: [-1, 0, 0], up: [0, -1, 0] },  // -X
      { target: [0, 1, 0], up: [0, 0, 1] },    // +Y
      { target: [0, -1, 0], up: [0, 0, -1] },  // -Y
      { target: [0, 0, 1], up: [0, -1, 0] },   // +Z
      { target: [0, 0, -1], up: [0, -1, 0] }   // -Z
    ];

    for (let i = 0; i < 6; i++) {
      // Attach this face of the cubemap
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_CUBE_MAP_POSITIVE_X + i,
        this.cubemapTexture,
        0
      );

      // Check framebuffer status on first face
      if (i === 0) {
        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
          console.error('Cubemap framebuffer not complete:', status);
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          return;
        }
      }

      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      // Create view matrix looking in the face direction
      const dir = faceDirections[i];
      const viewMatrix = lookAtMatrix(
        position.x, position.y, position.z,
        position.x + dir.target[0], position.y + dir.target[1], position.z + dir.target[2],
        dir.up[0], dir.up[1], dir.up[2]
      );

      // Render the scene (without the speed boost, and don't bind cubemap since we're rendering to it)
      this.renderSceneGeometry(viewMatrix, cubemapProjection, position, false, false);
    }

    // Restore default framebuffer
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private renderSceneGeometry(
    viewMatrix: Float32Array,
    projectionMatrix: Float32Array,
    viewPos: { x: number; y: number; z: number },
    includeSpeedBoost: boolean,
    bindCubemap: boolean = true
  ): void {
    const gl = this.gl;

    gl.useProgram(this.program);

    const identityMatrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1
    ]);

    gl.uniformMatrix4fv(this.uniforms.projection, false, projectionMatrix);
    gl.uniformMatrix4fv(this.uniforms.view, false, viewMatrix);
    gl.uniformMatrix4fv(this.uniforms.model, false, identityMatrix);

    gl.uniform3fv(this.uniforms.ambientColor, this.ambientColor);
    gl.uniform3f(this.uniforms.playerLightPos, viewPos.x, viewPos.y, viewPos.z);
    gl.uniform3fv(this.uniforms.playerLightColor, this.playerLightColor);
    gl.uniform1f(this.uniforms.playerLightRadius, this.playerLightEnabled ? this.playerLightRadius : 0);

    gl.uniform3f(this.uniforms.viewPos, viewPos.x, viewPos.y, viewPos.z);
    gl.uniform1i(this.uniforms.useSpecular, 0);
    gl.uniform1i(this.uniforms.useCubemap, 0);

    // Bind cubemap to unit 6 (needed to avoid sampler type mismatch errors)
    // Use dummy cubemap when rendering TO the real cubemap to avoid feedback
    gl.activeTexture(gl.TEXTURE6);
    // Explicitly unbind any 2D texture first to avoid type conflict
    gl.bindTexture(gl.TEXTURE_2D, null);
    if (bindCubemap && this.cubemapTexture) {
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.cubemapTexture);
    } else if (this.dummyCubemap) {
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.dummyCubemap);
    }

    // Bind dungeon grid texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.dungeonTexture);
    gl.uniform1i(this.uniforms.dungeonGrid, 0);
    gl.uniform2f(this.uniforms.dungeonSize, this.dungeonWidth, this.dungeonHeight);
    gl.uniform1f(this.uniforms.cellSize, this.cellSize);

    // Bind wall textures
    for (let i = 0; i < 4; i++) {
      gl.activeTexture(gl.TEXTURE2 + i);
      gl.bindTexture(gl.TEXTURE_2D, this.wallTextures[i]);
      gl.uniform1i(this.uniforms.wallTextures[i], 2 + i);
    }

    // Update star lights
    let lightIndex = 0;
    for (let i = 0; i < this.stars.length; i++) {
      const star = this.stars[i];
      if (!star.collected && lightIndex < this.maxLights) {
        this.starLightPositions[lightIndex * 3] = star.position.x;
        this.starLightPositions[lightIndex * 3 + 1] = 1.0;
        this.starLightPositions[lightIndex * 3 + 2] = star.position.z;

        const color = this.starColors[i] || [1, 1, 0.2];
        this.starLightColors[lightIndex * 3] = color[0] * 0.5;
        this.starLightColors[lightIndex * 3 + 1] = color[1] * 0.5;
        this.starLightColors[lightIndex * 3 + 2] = color[2] * 0.5;
        lightIndex++;
      }
    }

    // Add speed boost spotlight (strobing through spectrum)
    if (this.speedBoost && !this.speedBoost.collected && lightIndex < this.maxLights) {
      this.starLightPositions[lightIndex * 3] = this.speedBoost.position.x;
      this.starLightPositions[lightIndex * 3 + 1] = 1.0; // Ceiling height
      this.starLightPositions[lightIndex * 3 + 2] = this.speedBoost.position.z;

      // Strobe through spectrum - cycle through hue based on time
      const strobeSpeed = 0.5; // Cycles per second (one full cycle every 2 seconds)
      const hue = (this.time * strobeSpeed * 360) % 360;
      const [r, g, b] = hslToRgb(hue, 1.0, 0.5);
      this.starLightColors[lightIndex * 3] = r * 0.7;
      this.starLightColors[lightIndex * 3 + 1] = g * 0.7;
      this.starLightColors[lightIndex * 3 + 2] = b * 0.7;
      lightIndex++;
    }

    // Update ceiling light flicker state
    for (let i = 0; i < this.ceilingLightFlicker.length; i++) {
      const flicker = this.ceilingLightFlicker[i];
      if (this.time >= flicker.nextToggle) {
        flicker.on = !flicker.on;
        if (flicker.on) {
          // On for ~4.5s on average (random 2-7s) → 90% duty cycle with 0.5s off
          flicker.nextToggle = this.time + 2 + Math.random() * 5;
        } else {
          // Off for ~0.5s
          flicker.nextToggle = this.time + 0.3 + Math.random() * 0.4;
          // Brief bright flash before going dark
          flicker.flashUntil = this.time + 0.06;
          // Notify listener that a ceiling light just went off
          if (this.onCeilingLightOff) {
            const light = this.ceilingLights[i];
            this.onCeilingLightOff(light.x, light.z);
          }
        }
      }
    }

    // Add ceiling lights (warm white, 2x brightness; brief flash when turning off)
    const ceilingLightStartIndex = lightIndex;
    for (let i = 0; i < this.ceilingLights.length && lightIndex < this.maxLights; i++) {
      const flicker = this.ceilingLightFlicker[i];
      const isFlashing = flicker && !flicker.on && this.time < flicker.flashUntil;
      if (!flicker || (!flicker.on && !isFlashing)) continue;

      const light = this.ceilingLights[i];
      this.starLightPositions[lightIndex * 3] = light.x;
      this.starLightPositions[lightIndex * 3 + 1] = 0.975; // Bottom of fixture
      this.starLightPositions[lightIndex * 3 + 2] = light.z;

      if (isFlashing) {
        // Bright flash: 3x normal brightness
        this.starLightColors[lightIndex * 3] = 3.6;
        this.starLightColors[lightIndex * 3 + 1] = 3.3;
        this.starLightColors[lightIndex * 3 + 2] = 2.4;
      } else {
        this.starLightColors[lightIndex * 3] = 1.2;
        this.starLightColors[lightIndex * 3 + 1] = 1.1;
        this.starLightColors[lightIndex * 3 + 2] = 0.8;
      }
      lightIndex++;
    }

    gl.uniform3fv(this.uniforms.starLightPositions, this.starLightPositions);
    gl.uniform3fv(this.uniforms.starLightColors, this.starLightColors);
    gl.uniform1i(this.uniforms.starLightCount, lightIndex);
    gl.uniform1f(this.uniforms.starLightRadius, this.starLightRadius);
    gl.uniform1i(this.uniforms.ceilingLightStartIndex, ceilingLightStartIndex);

    gl.uniform3fv(this.uniforms.debugLightPos, this.debugLightPos);
    gl.uniform3fv(this.uniforms.debugLightColor, this.debugLightColor);
    gl.uniform1f(this.uniforms.debugLightRadius, this.debugLightRadius);
    gl.uniform1i(this.uniforms.debugLightEnabled, this.debugLightEnabled ? 1 : 0);

    // Draw walls
    gl.uniform1i(this.uniforms.useTexture, 0);
    gl.uniform1i(this.uniforms.useWallTexture, this.texturesLoaded ? 1 : 0);
    gl.uniform3fv(this.uniforms.color, [0.5, 0.5, 0.5]);

    if (this.wallNorthGeometry) {
      gl.bindVertexArray(this.wallNorthGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.wallNorthGeometry.vertexCount);
    }
    if (this.wallSouthGeometry) {
      gl.bindVertexArray(this.wallSouthGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.wallSouthGeometry.vertexCount);
    }
    if (this.wallEastGeometry) {
      gl.bindVertexArray(this.wallEastGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.wallEastGeometry.vertexCount);
    }
    if (this.wallWestGeometry) {
      gl.bindVertexArray(this.wallWestGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.wallWestGeometry.vertexCount);
    }

    // Draw floor
    gl.uniform1i(this.uniforms.useWallTexture, 0);
    if (this.floorGeometry) {
      if (this.texturesLoaded && this.floorTexture) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.floorTexture);
        gl.uniform1i(this.uniforms.surfaceTexture, 1);
        gl.uniform1i(this.uniforms.useTexture, 1);
      } else {
        gl.uniform1i(this.uniforms.useTexture, 0);
        gl.uniform3fv(this.uniforms.color, this.floorColor);
      }
      gl.bindVertexArray(this.floorGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.floorGeometry.vertexCount);
    }

    // Draw ceiling
    if (this.ceilingGeometry) {
      if (this.texturesLoaded && this.ceilingTexture) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.ceilingTexture);
        gl.uniform1i(this.uniforms.surfaceTexture, 1);
        gl.uniform1i(this.uniforms.useTexture, 1);
      } else {
        gl.uniform1i(this.uniforms.useTexture, 0);
        gl.uniform3fv(this.uniforms.color, this.ceilingColor);
      }
      gl.bindVertexArray(this.ceilingGeometry.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.ceilingGeometry.vertexCount);
    }

    // Draw stars
    gl.uniform1i(this.uniforms.useTexture, 0);
    gl.uniform1i(this.uniforms.useWallTexture, 0);
    if (this.starGeometry) {
      gl.bindVertexArray(this.starGeometry.vao);

      for (let i = 0; i < this.stars.length; i++) {
        const star = this.stars[i];
        if (star.collected) continue;

        const color = this.starColors[i] || [1, 1, 0.2];
        gl.uniform3fv(this.uniforms.color, color);

        const rotation = star.rotation + this.time;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);

        const modelMatrix = new Float32Array([
          cos, 0, -sin, 0,
          0, 1, 0, 0,
          sin, 0, cos, 0,
          star.position.x, star.position.y, star.position.z, 1
        ]);

        gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
        gl.drawArrays(gl.TRIANGLES, 0, this.starGeometry.vertexCount);
      }
    }

    // Draw speed boost (only in main render, not cubemap)
    if (includeSpeedBoost && this.speedBoostGeometry && this.speedBoost && !this.speedBoost.collected) {
      gl.bindVertexArray(this.speedBoostGeometry.vao);

      // Enable cubemap reflection
      gl.uniform1i(this.uniforms.useCubemap, 1);
      gl.uniform1f(this.uniforms.reflectivity, 0.85);

      // Bind cubemap to texture unit 6
      gl.activeTexture(gl.TEXTURE6);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.cubemapTexture);
      gl.uniform1i(this.uniforms.cubemap, 6);

      // Slight tint color (mostly let reflection show through)
      gl.uniform3f(this.uniforms.color, 0.95, 0.95, 1.0);

      const boost = this.speedBoost;
      const rotation = boost.rotation + this.time * 1.5;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);

      const modelMatrix = new Float32Array([
        cos, 0, -sin, 0,
        0, 1, 0, 0,
        sin, 0, cos, 0,
        boost.position.x, boost.position.y, boost.position.z, 1
      ]);

      gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
      gl.drawArrays(gl.TRIANGLES, 0, this.speedBoostGeometry.vertexCount);

      gl.uniform1i(this.uniforms.useCubemap, 0);
    }

    // Draw ceiling light fixtures
    if (this.ceilingLightGeometry && this.ceilingLights.length > 0) {
      gl.bindVertexArray(this.ceilingLightGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useSpecular, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);

      for (let i = 0; i < this.ceilingLights.length; i++) {
        const light = this.ceilingLights[i];
        const flicker = this.ceilingLightFlicker[i];
        const isOn = flicker ? flicker.on : true;
        const isFlashing = flicker && !flicker.on && this.time < flicker.flashUntil;

        if (isFlashing) {
          gl.uniform3f(this.uniforms.color, 4.0, 3.8, 3.0);
        } else if (isOn) {
          gl.uniform3f(this.uniforms.color, 1.5, 1.425, 1.2);
        } else {
          gl.uniform3f(this.uniforms.color, 1.0, 0.95, 0.8);
        }

        const modelMatrix = new Float32Array([
          1, 0, 0, 0,
          0, 1, 0, 0,
          0, 0, 1, 0,
          light.x, 1.0, light.z, 1
        ]);
        gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
        gl.drawArrays(gl.TRIANGLES, 0, this.ceilingLightGeometry.vertexCount);
      }
    }

    // Draw web trap
    if (this.webTrapGeometry && this.webTrap) {
      gl.bindVertexArray(this.webTrapGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);

      // Pulsing cyan-white glow, dimmer after triggered
      const pulse = 0.7 + 0.2 * Math.sin(this.time * 3);
      const brightness = this.webTrap.triggered ? pulse * 0.3 : pulse;
      gl.uniform3f(this.uniforms.color, brightness * 0.7, brightness, brightness);

      const modelMatrix = new Float32Array([
        1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        this.webTrap.position.x, this.webTrap.position.y, this.webTrap.position.z, 1
      ]);
      gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
      gl.drawArrays(gl.TRIANGLES, 0, this.webTrapGeometry.vertexCount);
    }

    // Draw breadcrumbs pickup (orange icosahedron)
    if (this.breadcrumbsPickupGeometry && this.breadcrumbs && !this.breadcrumbs.collected) {
      gl.bindVertexArray(this.breadcrumbsPickupGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);
      gl.uniform3f(this.uniforms.color, 1.0, 0.6, 0.1);

      const bc = this.breadcrumbs;
      const rotation = bc.rotation + this.time;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const modelMatrix = new Float32Array([
        cos, 0, -sin, 0,
        0, 1, 0, 0,
        sin, 0, cos, 0,
        bc.position.x, bc.position.y, bc.position.z, 1
      ]);
      gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
      gl.drawArrays(gl.TRIANGLES, 0, this.breadcrumbsPickupGeometry.vertexCount);
    }

    // Draw footprints (after breadcrumbs collected)
    if (this.footprintGeometry && this.breadcrumbs && this.breadcrumbs.collected) {
      gl.bindVertexArray(this.footprintGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);
      gl.uniform3f(this.uniforms.color, 0.8, 0.5, 0.1);

      for (const [key, yaw] of this.breadcrumbs.footprints) {
        const [cx, cz] = key.split(',').map(Number);
        const wx = (cx + 0.5) * this.cellSize;
        const wz = (cz + 0.5) * this.cellSize;

        const cos = Math.cos(yaw);
        const sin = Math.sin(yaw);
        const modelMatrix = new Float32Array([
          cos, 0, -sin, 0,
          0, 1, 0, 0,
          sin, 0, cos, 0,
          wx, 0, wz, 1
        ]);
        gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
        gl.drawArrays(gl.TRIANGLES, 0, this.footprintGeometry.vertexCount);
      }
    }

    // Draw arrow pickup (flat arrow shape)
    if (this.floatingArrowGeometry && this.arrow && !this.arrow.collected) {
      gl.bindVertexArray(this.floatingArrowGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);
      gl.uniform3f(this.uniforms.color, 0.1, 0.9, 1.0);

      const ar = this.arrow;
      // Y-spin, then tilt upright (90° around X so flat XZ becomes vertical XY)
      const rotation = ar.rotation + this.time;
      const cy = Math.cos(rotation);
      const sy = Math.sin(rotation);
      // Ry * Rx(90°): Rx rotates Y→Z, Z→-Y
      // Combined: col0 = [cy, 0, sy], col1 = [sy*(-1), 0, cy*(-1)] wait...
      // Ry = [cy 0 -sy; 0 1 0; sy 0 cy], Rx90 = [1 0 0; 0 0 1; 0 -1 0]
      // M = Ry * Rx90:
      //   col0: Ry * [1,0,0] = [cy, 0, sy]
      //   col1: Ry * [0,0,-1] = [sy, 0, -cy]  (since Rx90 col1 = [0,0,-1])
      //   col2: Ry * [0,1,0] = [0, 1, 0]
      const modelMatrix = new Float32Array([
        cy,  0,  sy, 0,
        sy,  0, -cy, 0,
         0,  1,   0, 0,
        ar.position.x, ar.position.y, ar.position.z, 1
      ]);
      gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
      gl.drawArrays(gl.TRIANGLES, 0, this.floatingArrowGeometry.vertexCount);
    }

    // Draw floating arrow (after arrow powerup collected)
    if (this.floatingArrowGeometry && this.floatingArrowPosition) {
      gl.bindVertexArray(this.floatingArrowGeometry.vao);
      gl.uniform1i(this.uniforms.useTexture, 0);
      gl.uniform1i(this.uniforms.useWallTexture, 0);
      gl.uniform1i(this.uniforms.useCubemap, 0);

      // Pulsing cyan with bob
      const pulse = 0.7 + 0.3 * Math.sin(this.time * 4);
      gl.uniform3f(this.uniforms.color, 0.1 * pulse, 0.9 * pulse, 1.0 * pulse);

      const pos = this.floatingArrowPosition;
      const bobY = pos.y + Math.sin(this.time * 2) * 0.03;
      const yaw = this.floatingArrowYaw;

      // Arrow geometry points along -Z, so rotate by yaw around Y
      const cos = Math.cos(yaw);
      const sin = Math.sin(yaw);
      const modelMatrix = new Float32Array([
        cos, 0, -sin, 0,
        0, 1, 0, 0,
        sin, 0, cos, 0,
        pos.x, bobY, pos.z, 1
      ]);
      gl.uniformMatrix4fv(this.uniforms.model, false, modelMatrix);
      gl.drawArrays(gl.TRIANGLES, 0, this.floatingArrowGeometry.vertexCount);
    }

    gl.bindVertexArray(null);
  }

  render(camera: Camera): void {
    const gl = this.gl;

    this.time += 0.016;  // Approximate frame time for rotation

    // Render cubemap from speed boost position (if speed boost exists and not collected)
    if (this.speedBoost && !this.speedBoost.collected) {
      this.renderToCubemap(this.speedBoost.position);
    }

    // Restore viewport for main render
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    // Render main scene with speed boost
    const viewMatrix = camera.getViewMatrix();
    this.renderSceneGeometry(viewMatrix, this.projectionMatrix, camera.position, true);
  }
}

// Convert HSL to RGB (h: 0-360, s: 0-1, l: 0-1) -> [r, g, b] each 0-1
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  return [r + m, g + m, b + m];
}

// Create a look-at view matrix
function lookAtMatrix(
  eyeX: number, eyeY: number, eyeZ: number,
  targetX: number, targetY: number, targetZ: number,
  upX: number, upY: number, upZ: number
): Float32Array {
  // Calculate forward vector (from eye to target)
  let fwdX = targetX - eyeX;
  let fwdY = targetY - eyeY;
  let fwdZ = targetZ - eyeZ;
  let fwdLen = Math.sqrt(fwdX * fwdX + fwdY * fwdY + fwdZ * fwdZ);
  fwdX /= fwdLen; fwdY /= fwdLen; fwdZ /= fwdLen;

  // Calculate right vector (forward x up)
  let rightX = fwdY * upZ - fwdZ * upY;
  let rightY = fwdZ * upX - fwdX * upZ;
  let rightZ = fwdX * upY - fwdY * upX;
  let rightLen = Math.sqrt(rightX * rightX + rightY * rightY + rightZ * rightZ);
  rightX /= rightLen; rightY /= rightLen; rightZ /= rightLen;

  // Calculate corrected up vector (right x forward)
  const corrUpX = rightY * fwdZ - rightZ * fwdY;
  const corrUpY = rightZ * fwdX - rightX * fwdZ;
  const corrUpZ = rightX * fwdY - rightY * fwdX;

  // Build view matrix (transpose of rotation * translation)
  return new Float32Array([
    rightX, corrUpX, -fwdX, 0,
    rightY, corrUpY, -fwdY, 0,
    rightZ, corrUpZ, -fwdZ, 0,
    -(rightX * eyeX + rightY * eyeY + rightZ * eyeZ),
    -(corrUpX * eyeX + corrUpY * eyeY + corrUpZ * eyeZ),
    (fwdX * eyeX + fwdY * eyeY + fwdZ * eyeZ),
    1
  ]);
}

// Create a perspective projection matrix
function perspectiveMatrix(out: Float32Array, fov: number, aspect: number, near: number, far: number): void {
  const f = 1.0 / Math.tan(fov / 2);
  const nf = 1 / (near - far);

  out[0] = f / aspect;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;

  out[4] = 0;
  out[5] = f;
  out[6] = 0;
  out[7] = 0;

  out[8] = 0;
  out[9] = 0;
  out[10] = (far + near) * nf;
  out[11] = -1;

  out[12] = 0;
  out[13] = 0;
  out[14] = 2 * far * near * nf;
  out[15] = 0;
}
